import { Button } from "@components/ui/Button"
import { DropdownMenu } from "@components/ui/DropdownMenu"
import { Input } from "@components/ui/Input"
import { DropdownItem } from "@components/ui/menu-helpers"
import { alerts } from "@features/alerts/alerts"
import { cn } from "@utils/tw"
import { useAppData, useRPCMutation } from "eddev/hooks"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useMemo, useRef, useState } from "react"
import { useNewsletterCookie } from "./useNewsletterCookie"
import { trackEvent } from "@features/tracking/tracking"

type Props = {
  onSuccess?: () => void
  onDismiss?: () => void
  dialog?: boolean
  defaultNewsletter?: string
}

function useNewsletterConfig() {
  const source = useAppData((data) => {
    return data.sffSettings?.newsletters?.newsletterConfig
  })

  return useMemo(() => {
    return (
      source?.map((group) => {
        return {
          title: group?.groupLabel!,
          items:
            group?.lists?.map((item) => {
              return {
                listId: item?.listId!,
                type: group.type as "sff" | "tff",
                title: item?.label!,
              }
            }) ?? [],
        }
      }) ?? []
    )
  }, [source])
}

export function NewsletterSubscribe(props: Props) {
  const newsletters = useNewsletterConfig()

  const ref = useRef<HTMLDivElement>(null)
  const [email, setEmail] = useState("")
  const [newsletterVals, setNewsletterVals] = useState<string[][]>(() => {
    let defaultListId = newsletters[0].items[0].listId
    if (props.defaultNewsletter) {
      for (const group of newsletters) {
        for (const item of group.items) {
          if (item.title === props.defaultNewsletter) {
            defaultListId = item.listId
          }
        }
      }
    }
    return newsletters.map((group) => {
      return group.items.some((item) => item.listId === defaultListId) ? [defaultListId] : []
    })
  })
  const newsletterValsFull = newsletterVals.flatMap((val) =>
    newsletters.flatMap((group) => group.items.filter((item) => val.includes(item.listId))),
  )

  const newsletterCookie = useNewsletterCookie()

  const signup = useRPCMutation("api.newsletter.signup", {
    onSuccess: () => {
      newsletterCookie.setSubscribed()
      trackEvent("Subscribed to Newsletter", {}, ref.current)
      props.onSuccess?.()
    },
  })

  let newslettersButtonLabel: ReactNode = <span className="text-fg-secondary">Choose a mailing list...</span>

  if (newsletterValsFull.length == 1) {
    newslettersButtonLabel = newsletterValsFull[0].title
  } else if (newsletterValsFull.length > 1) {
    newslettersButtonLabel = newsletterValsFull.length + " newsletters selected"
  }

  function submit() {
    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alerts.displayAlert({
        title: "Invalid email address",
        description: "Please enter a valid email address.",
        confirmButton: "Okay",
      })
      return
    }
    if (newsletterValsFull.length === 0) {
      alerts.displayAlert({
        title: "No newsletters selected",
        description: "Please select at least one mailing list before continuing.",
        confirmButton: "Okay",
      })
      return
    }
    signup.mutate({ email, lists: newsletterValsFull })
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={signup.isSuccess ? "success" : "form"}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        ref={ref}
      >
        {signup.isSuccess && !props.onSuccess ? (
          <>
            <div className="bg-bg-low rounded-md flex items-center justify-between gap-3 p-3">
              <div className="type-body-m pl-2">Thank you for signing up!</div>
              <Button variant="primary" onClick={() => signup.reset()}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
            className="flex flex-col"
          >
            <div className="flex flex-col gap-3">
              <Input
                placeholder="Your email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={signup.isPending}
                enterKeyHint="done"
              />
              <DropdownMenu
                items={newsletters.flatMap((group): DropdownItem[] => {
                  return [
                    {
                      type: "heading",
                      label: group.title,
                    },
                    ...group.items.map(
                      (item): DropdownItem => ({
                        type: "checkbox",
                        label: item.title,
                        checked: newsletterVals.flatMap((val) => val).includes(item.listId),
                        onCheckedChange: (checked) => {
                          const newVals = [...newsletterVals]
                          const groupIndex = newsletters.findIndex((g) => g.title === group.title)
                          if (checked) {
                            newVals[groupIndex].push(item.listId)
                          } else {
                            newVals[groupIndex] = newVals[groupIndex].filter((val) => val !== item.listId)
                          }
                          setNewsletterVals(newVals)
                        },
                      }),
                    ),
                  ]
                })}
                trigger={
                  <Button rightIcon="caretDown" className="justify-between" disabled={signup.isPending}>
                    {newslettersButtonLabel}
                  </Button>
                }
              />
            </div>
            <div className={cn("flex mt-4 gap-3", props.dialog && "justify-end [&>button]:flex-1")}>
              {props.dialog && (
                <Button
                  variant="outline"
                  shape="branded"
                  onClick={() => {
                    newsletterCookie.setDismissed()
                    props.onDismiss?.()
                  }}
                >
                  No thanks
                </Button>
              )}
              <Button className="w-fit" variant="primary" shape="branded" type="submit" loading={signup.isPending}>
                Subscribe
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </AnimatePresence>
  )
}
